/**
 * Canonical buyer-facing vehicle title builder (M1).
 *
 * Produces: Year Make Model Trim
 *
 * Parity required with:
 * server/vehicleIntelligence/vehicleIdentity/canonicalVehicleTitle.js
 */
import {
  PRESERVED_DISPLAY_TOKENS,
  REMOVABLE_EQUIPMENT_TOKENS,
  COLOR_ONLY_TOKENS,
  FUEL_TYPE_TOKENS,
  FUEL_TYPE_PHRASES,
  MILEAGE_UNIT_TOKENS,
  TRAILING_POLLUTION_TOKENS,
  TRANSMISSION_TOKENS,
} from "./canonicalVehicleTitleConstants";

const DEFAULT_FALLBACK = "Vehículo usado";

function toPresentString(value) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
    return null;
  }
  return text;
}

function collapseWhitespace(value) {
  return value.replace(/\s+/g, " ").trim();
}

function stripEdgePunctuation(value) {
  return value.replace(/^[\s.,;:/\-–—]+|[\s.,;:/\-–—]+$/g, "").trim();
}

function normalizeComparable(value) {
  return collapseWhitespace(
    String(value || "")
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .toLowerCase()
  );
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function removeByPattern(text, pattern) {
  return collapseWhitespace(text.replace(pattern, " "));
}

function removeStandaloneTokens(text, tokens) {
  let result = text;
  const sorted = [...tokens].sort((a, b) => b.length - a.length);
  for (const token of sorted) {
    const pattern = new RegExp(`\\b${escapeRegExp(token)}\\b`, "giu");
    result = removeByPattern(result, pattern);
  }
  return stripEdgePunctuation(result);
}

function removePrices(text) {
  let result = text;
  result = removeByPattern(
    result,
    /\b(?:precio|valor)\s*:?\s*(?:cop\s*)?\$?\s*[\d.,]+(?:\s*(?:cop|millones?))?\b/giu
  );
  result = removeByPattern(result, /\$\s*[\d]{1,3}(?:[.,]\d{3})+(?:[.,]\d+)?\b/gu);
  result = removeByPattern(result, /\b(?:cop\s*)?[\d]{1,3}(?:[.,]\d{3}){2,}(?:[.,]\d+)?\s*(?:cop)?\b/giu);
  result = removeByPattern(result, /\b[\d]+(?:[.,]\d+)?\s*millones?\b/giu);
  result = removeByPattern(result, /\b(?:cop\s*)?[\d]{1,3}(?:[.,]\d{3})+(?:[.,]\d+)?\s*cop\b/giu);
  return stripEdgePunctuation(result);
}

function removeMileage(text) {
  let result = text;
  result = removeByPattern(result, /\bkilometraje\s*:?\s*[\d.,]+\b/giu);
  result = removeByPattern(
    result,
    /\b[\d.,]+\s*(?:mil\s+)?(?:km|kms|kil[oó]metros?)\b/giu
  );
  return stripEdgePunctuation(result);
}

function removeBareMileageClauses(text) {
  let result = text;
  result = removeByPattern(
    result,
    /\b(?:con|de|a)\s+[\d]{1,3}(?:[.,]\d{3})+(?:\s*(?:km|kms|kil[oó]metros?))?\b/giu
  );
  return stripEdgePunctuation(result);
}

function removeFuelTypePhrases(text) {
  let result = text;
  for (const phrase of FUEL_TYPE_PHRASES) {
    const pattern = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, "giu");
    result = removeByPattern(result, pattern);
  }
  return stripEdgePunctuation(result);
}

function removeColombianEngineDisplacement(text) {
  return stripEdgePunctuation(removeByPattern(text, /\b[12]\.\d{3}\b/gu));
}

function removePowerRatings(text) {
  let result = text;
  result = removeByPattern(result, /\b\d{1,4}\s*KW\b/giu);
  result = removeByPattern(result, /\b\d{1,4}\s*HP\b/giu);
  result = removeByPattern(result, /\b\d{1,4}\s*CV\b/giu);
  return stripEdgePunctuation(result);
}

function removeStandaloneEquipmentCodes(text) {
  return stripEdgePunctuation(removeByPattern(text, /\b\d[A-Z]{2}\b/gu));
}

function removePipeDelimitedMetadata(text) {
  let result = collapseWhitespace(text);
  result = removeByPattern(result, /\s*\|.*$/u);
  result = removeByPattern(result, /^\|\s*/u);
  return stripEdgePunctuation(result);
}

function isColorOnlyIdentity(text) {
  const normalized = normalizeComparable(text);
  if (!normalized) return true;
  const colorSet = new Set(COLOR_ONLY_TOKENS.map((token) => normalizeComparable(token)));
  return colorSet.has(normalized);
}

function isIdentityNoiseOnly(text) {
  const tokens = normalizeComparable(text).split(" ").filter(Boolean);
  if (!tokens.length) return true;
  const noise = new Set([
    "km",
    "kms",
    "kilometros",
    "kilometraje",
    "con",
    "de",
    "a",
    "y",
    "o",
  ]);
  return tokens.every((token) => noise.has(token));
}

function removeEngineDisplacement(text) {
  let result = text;
  result = removeColombianEngineDisplacement(result);
  result = removeByPattern(result, /\b\d{4}\s*cc\b/giu);
  result = removeByPattern(result, /\bV\d\s*\d(?:[.,]\d)?\b/giu);
  result = removeByPattern(
    result,
    /\b\d(?:[.,]\d)\s*(?:L|litros?)\b/giu
  );
  result = removeByPattern(result, /\b\d(?:[.,]\d)\s*T(?:urbo)?\b/giu);
  result = removeByPattern(result, /(?<![\w-])\d(?:[.,]\d)(?![\w-])/gu);
  return stripEdgePunctuation(result);
}

function removeTrailingPollution(text) {
  let result = text;
  let changed = true;
  while (changed) {
    changed = false;
    for (const token of TRAILING_POLLUTION_TOKENS) {
      const pattern = new RegExp(`(?:\\s+|-)${escapeRegExp(token)}$`, "iu");
      if (pattern.test(result)) {
        result = result.replace(pattern, "");
        changed = true;
      }
    }
    result = stripEdgePunctuation(result);
  }
  return result;
}

function removeDuplicateYear(text, year) {
  const normalizedYear = toPresentString(year);
  if (!text || !normalizedYear || !/^\d{4}$/.test(normalizedYear)) return text;
  const pattern = new RegExp(`\\b${escapeRegExp(normalizedYear)}\\b`, "gu");
  return stripEdgePunctuation(removeByPattern(text, pattern));
}

function removeDuplicateMake(text, make) {
  const presentMake = toPresentString(make);
  if (!text || !presentMake) return text;
  const pattern = new RegExp(`^${escapeRegExp(presentMake)}\\b\\s*`, "iu");
  return stripEdgePunctuation(text.replace(pattern, ""));
}

function removeRepeatedModelPhrase(text) {
  const tokens = collapseWhitespace(text).split(" ").filter(Boolean);
  if (tokens.length < 2) return text;

  for (let size = Math.floor(tokens.length / 2); size >= 1; size -= 1) {
    for (let index = 0; index + size * 2 <= tokens.length; index += 1) {
      const left = tokens.slice(index, index + size).join(" ");
      const right = tokens.slice(index + size, index + size * 2).join(" ");
      if (normalizeComparable(left) === normalizeComparable(right)) {
        return tokens.slice(0, index + size).concat(tokens.slice(index + size * 2)).join(" ");
      }
    }
  }

  return text;
}

function isAllUpperAlpha(value) {
  return /^[A-ZÀ-Ý]+$/.test(value);
}

function isAllLowerAlpha(value) {
  return /^[a-zà-ÿ]+$/.test(value);
}

function hasIntentionalMixedCase(value) {
  return /[a-zà-ÿ]/.test(value) && /[A-ZÀ-Ý]/.test(value);
}

function titleCaseWord(word) {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export function normalizeVehicleDisplayToken(value) {
  const present = toPresentString(value);
  if (!present) return null;

  const collapsed = collapseWhitespace(present);
  const upperKey = collapsed.toUpperCase();
  const normalizedKey = upperKey.replace(/\s+/g, " ");

  if (PRESERVED_DISPLAY_TOKENS[normalizedKey]) {
    return PRESERVED_DISPLAY_TOKENS[normalizedKey];
  }
  if (PRESERVED_DISPLAY_TOKENS[collapsed]) {
    return PRESERVED_DISPLAY_TOKENS[collapsed];
  }

  if (collapsed.includes("-")) {
    return collapsed
      .split("-")
      .map((part) => normalizeVehicleDisplayToken(part))
      .filter(Boolean)
      .join("-");
  }

  if (hasIntentionalMixedCase(collapsed) && !isAllUpperAlpha(collapsed)) {
    return collapsed;
  }

  // Short all-caps trim codes (TXL, EXL, LX) — not 4-letter model names like ONIX/KWID.
  if (isAllUpperAlpha(collapsed) && collapsed.length >= 2 && collapsed.length <= 3) {
    return collapsed;
  }

  if (/^[A-Za-z]\d{1,2}$/.test(collapsed) || /^\d{3,4}$/.test(collapsed)) {
    if (PRESERVED_DISPLAY_TOKENS[upperKey]) return PRESERVED_DISPLAY_TOKENS[upperKey];
    return collapsed.toUpperCase() === collapsed ? collapsed : collapsed;
  }

  if (isAllUpperAlpha(collapsed) || isAllLowerAlpha(collapsed)) {
    return titleCaseWord(collapsed);
  }

  return collapsed;
}

function applyDisplayCasing(text) {
  const present = toPresentString(text);
  if (!present) return null;
  return collapseWhitespace(
    present
      .split(/\s+/)
      .map((token) => normalizeVehicleDisplayToken(token))
      .filter(Boolean)
      .join(" ")
  );
}

function normalizeYear(year) {
  if (year == null || year === "") return null;
  const numeric = Number(year);
  if (!Number.isFinite(numeric)) return null;
  const rounded = Math.floor(numeric);
  if (rounded < 1900 || rounded > 2100) return null;
  return String(rounded);
}

export function sanitizeVehicleMake(value) {
  const present = toPresentString(value);
  if (!present) return null;
  return applyDisplayCasing(stripEdgePunctuation(collapseWhitespace(present)));
}

export function sanitizeVehicleModel(value, context = {}) {
  const present = toPresentString(value);
  if (!present) return null;

  let result = collapseWhitespace(present);
  result = removePipeDelimitedMetadata(result);
  result = removePrices(result);
  result = removeMileage(result);
  result = removeBareMileageClauses(result);
  result = removeFuelTypePhrases(result);
  result = removePowerRatings(result);
  result = removeStandaloneEquipmentCodes(result);
  result = removeStandaloneTokens(result, REMOVABLE_EQUIPMENT_TOKENS);
  result = removeStandaloneTokens(result, TRANSMISSION_TOKENS);
  result = removeStandaloneTokens(result, FUEL_TYPE_TOKENS);
  result = removeEngineDisplacement(result);
  result = removeTrailingPollution(result);
  result = removeDuplicateYear(result, context.year);
  result = removeDuplicateMake(result, context.make);
  result = removeRepeatedModelPhrase(result);
  result = stripEdgePunctuation(result);

  return applyDisplayCasing(result) || null;
}

export function sanitizeVehicleTrim(value, context = {}) {
  const present = toPresentString(value);
  if (!present) return null;

  let result = collapseWhitespace(present);
  result = removePipeDelimitedMetadata(result);
  result = removePrices(result);
  result = removeMileage(result);
  result = removeFuelTypePhrases(result);
  result = removeStandaloneTokens(result, REMOVABLE_EQUIPMENT_TOKENS);
  result = removeStandaloneTokens(result, FUEL_TYPE_TOKENS);
  result = removeStandaloneTokens(result, TRANSMISSION_TOKENS);
  result = removeStandaloneTokens(result, MILEAGE_UNIT_TOKENS);
  result = removePowerRatings(result);
  result = removeStandaloneEquipmentCodes(result);
  result = removeEngineDisplacement(result);
  result = removeTrailingPollution(result);
  result = removeDuplicateYear(result, context.year);
  result = stripEdgePunctuation(result);

  const sanitized = applyDisplayCasing(result) || null;
  if (!sanitized || isIdentityNoiseOnly(sanitized) || isColorOnlyIdentity(sanitized)) return null;
  return sanitized;
}

function trimIsSuffixOfModel(model, trim) {
  const normalizedModel = normalizeComparable(model);
  const normalizedTrim = normalizeComparable(trim);
  if (!normalizedModel || !normalizedTrim) return false;
  if (normalizedTrim.length < 2) return false;
  return (
    normalizedModel === normalizedTrim ||
    normalizedModel.endsWith(` ${normalizedTrim}`) ||
    normalizedModel.endsWith(`-${normalizedTrim}`)
  );
}

export function dedupeVehicleIdentityParts({ year = null, make = null, model = null, trim = null } = {}) {
  const normalizedYear = normalizeYear(year);
  const sanitizedMake = sanitizeVehicleMake(make);
  const sanitizedModel = sanitizeVehicleModel(model, { year: normalizedYear, make: sanitizedMake });
  let sanitizedTrim = sanitizeVehicleTrim(trim, { year: normalizedYear, make: sanitizedMake });

  if (sanitizedModel && sanitizedTrim && trimIsSuffixOfModel(sanitizedModel, sanitizedTrim)) {
    sanitizedTrim = null;
  }

  return {
    year: normalizedYear,
    make: sanitizedMake,
    model: sanitizedModel,
    trim: sanitizedTrim,
  };
}

export function buildCanonicalVehicleTitle({
  year,
  make,
  model,
  trim,
  fallback = DEFAULT_FALLBACK,
} = {}) {
  const parts = dedupeVehicleIdentityParts({ year, make, model, trim });
  const titleParts = [parts.year, parts.make, parts.model, parts.trim].filter(Boolean);
  if (!titleParts.length) {
    return toPresentString(fallback) || DEFAULT_FALLBACK;
  }
  return titleParts.join(" ");
}

function firstPresent(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (!text || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
      continue;
    }
    return value;
  }
  return null;
}

export function selectVehicleYear(listing = {}) {
  return firstPresent(listing.year, listing.modelYear, listing.model_year, listing.yearRaw, listing.year_raw);
}

export function selectVehicleMake(listing = {}) {
  return firstPresent(
    listing.lookupMake,
    listing.makeLookupName,
    listing.makeLabel,
    listing.make_label,
    listing.makeNormalized,
    listing.make_normalized,
    listing.makeRaw,
    listing.make_raw,
    listing.make,
    listing.brand
  );
}

export function selectVehicleModel(listing = {}) {
  return firstPresent(
    listing.lookupModel,
    listing.modelLookupName,
    listing.modelLabel,
    listing.model_label,
    listing.modelNormalized,
    listing.model_normalized,
    listing.modelRaw,
    listing.model_raw,
    listing.model
  );
}

export function selectVehicleTrim(listing = {}) {
  return firstPresent(
    listing.lookupTrim,
    listing.trimLookupName,
    listing.trimLabel,
    listing.trim_label,
    listing.trimNormalized,
    listing.trim_normalized,
    listing.trimRaw,
    listing.trim_raw,
    listing.trim,
    listing.versionNormalized,
    listing.version_normalized,
    listing.versionRaw,
    listing.version_raw,
    listing.version
  );
}

/**
 * Maps a client listing record to the canonical buyer-facing title (M3).
 * Does not use dealer headlines (`title`, `listing_title`) as identity sources.
 */
export function buildCanonicalTitleFromListing(listing = {}, options = {}) {
  const record = listing && typeof listing === "object" && !Array.isArray(listing) ? listing : {};
  return buildCanonicalVehicleTitle({
    year: selectVehicleYear(record),
    make: selectVehicleMake(record),
    model: selectVehicleModel(record),
    trim: selectVehicleTrim(record),
    fallback: options.fallback ?? DEFAULT_FALLBACK,
  });
}

export { DEFAULT_FALLBACK };
